import { format } from "date-fns/format";
import React from "react";
import { toast } from "react-hot-toast";

import { ErrorToast } from "../../../components";
import {
  MovementTypeOption,
  MovementTypeOptionsResult,
  Player,
  Session,
} from "../../../types";

export const handleVisualizeClick = (
  player: Player,
  session: Session,
  setVisualizerInfo: (info: any) => void,
  setIsVisualizerModalOpen: (isOpen: boolean) => void,
  movementTypeId?: number,
) => {
  const info = {
    playerName: `${player.firstName} ${player.lastName}`,
    orgPlayerId: String(player.orgPlayerId ?? ""),
    domHand: player.hand ?? session.hand ?? "",
    sessionId: session.id,
    sessionDate: format(new Date(session.sessionDate), "M/d/yyyy"),
    movementTypeId: movementTypeId?.toString(),
  };
  setVisualizerInfo(info);
  setIsVisualizerModalOpen(true);
};

// Helper function to validate basic data requirements
const validateBasicData = (
  data: any,
  contextData: any,
  contextType: "player" | "session",
): boolean => {
  if (!data) {
    showVisualizerErrorToast("Unable to open visualizer. Data is missing.");
    return false;
  }

  if (!contextData) {
    const dataType = contextType === "player" ? "Player" : "Session";
    showVisualizerErrorToast(
      `Unable to open visualizer. ${dataType} data is not available. Please refresh the page and try again.`,
    );
    return false;
  }

  return true;
};

// Helper function to extract player and session based on context type
const extractPlayerAndSession = (
  data: any,
  contextData: any,
  contextType: "player" | "session",
): { player: any; session: any } => {
  if (contextType === "player") {
    // PlayerDetails context: data is Session, contextData is Player
    return { player: contextData, session: data };
  } else {
    // SessionDetails context: data is Player, contextData is Session
    return { player: data, session: contextData };
  }
};

// Helper function to validate player data structure
const validatePlayerData = (player: any): boolean => {
  if (
    !player?.id ||
    !player?.firstName ||
    !player?.lastName ||
    !player?.orgPlayerId
  ) {
    showVisualizerErrorToast("Unable to open visualizer. Invalid player data.");
    return false;
  }
  return true;
};

// Helper function to validate session data structure
const validateSessionData = (session: any): boolean => {
  if (!session?.id || !session?.sessionDate) {
    showVisualizerErrorToast(
      "Unable to open visualizer. Invalid session data.",
    );
    return false;
  }
  return true;
};

export const createOnVisualizeClick = (
  contextData: any,
  contextType: "player" | "session",
  setVisualizerInfo: any,
  setIsVisualizerModalOpen: any,
  movementTypeId?: number,
) => {
  return (data: any) => {
    // Validate basic data requirements
    if (!validateBasicData(data, contextData, contextType)) {
      return;
    }

    // Extract player and session based on context type
    const { player, session } = extractPlayerAndSession(
      data,
      contextData,
      contextType,
    );

    // Validate player and session data structures
    if (!validatePlayerData(player) || !validateSessionData(session)) {
      return;
    }

    // All validations passed, proceed with visualization
    handleVisualizeClick(
      player,
      session,
      setVisualizerInfo,
      setIsVisualizerModalOpen,
      movementTypeId,
    );
  };
};

export const showVisualizerErrorToast = (message: string) =>
  toast.custom(
    React.createElement(ErrorToast, { message, classNames: "mt-modal" }),
    {
      id: "visualizerError",
      duration: 4000,
    },
  );

// Helper function to build movement type options for visualization
export const buildMovementTypeOptions = (
  movementTypes: string[],
  contextData: any,
  contextType: "player" | "session",
  setVisualizerInfo: any,
  setIsVisualizerModalOpen: any,
): MovementTypeOptionsResult => {
  const canVisualizePitching = movementTypes.includes("baseballPitching");
  const canVisualizeHitting = movementTypes.includes("baseballHitting");
  const canVisualize = canVisualizePitching || canVisualizeHitting;

  const movementTypeOptions: MovementTypeOption[] = [];

  if (canVisualizePitching) {
    movementTypeOptions.push({
      slug: "baseballPitching",
      label: "Pitching",
      handler: createOnVisualizeClick(
        contextData,
        contextType,
        setVisualizerInfo,
        setIsVisualizerModalOpen,
        2, // baseball-pitching
      ),
    });
  }

  if (canVisualizeHitting) {
    movementTypeOptions.push({
      slug: "baseballHitting",
      label: "Hitting",
      handler: createOnVisualizeClick(
        contextData,
        contextType,
        setVisualizerInfo,
        setIsVisualizerModalOpen,
        1, // baseball-hitting
      ),
    });
  }

  // Use single handler if only one movement type, otherwise use options array for dropdown
  const singleMovementHandler =
    movementTypeOptions.length === 1
      ? movementTypeOptions[0].handler
      : undefined;

  return {
    movementTypeOptions,
    singleMovementHandler,
    canVisualize,
  };
};
